// iter711 — Period Locks settings page. Central, super-admin-friendly
// hub for locking / unlocking month-end periods. Surfaces every lock
// with who created it + when + optional notes, plus a one-click "Lock
// last month" CTA so the bookkeeper can seal a period in 2 taps.

import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Unlock, ShieldCheck, AlertTriangle, CalendarCheck2, Sparkles, X, Timer, Bell, Play } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuthStore } from '@/stores';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const DOM = Array.from({ length: 28 }, (_, i) => i + 1); // 1..28 (every month has these)

function lastNMonths(n) {
  const out = [];
  const now = new Date();
  // Anchor to the FIRST day of "n months ago" so the YYYY-MM math is timezone-independent.
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function ymToLabel(ym) {
  const [y, m] = (ym || '').split('-');
  if (!y || !m) return ym;
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

export default function PeriodLocksPage() {
  const user = useAuthStore((s) => s.user);
  const canLock = user?.role === 'admin' || user?.is_super_admin;
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyYm, setBusyYm] = useState(null);
  const [customYm, setCustomYm] = useState('');
  const [customNotes, setCustomNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/bank-statements/monthly-completion?months_back=24');
      setMonthly(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load lock status');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const months = monthly?.months || [];
  const lockedMonths = useMemo(() => months.filter((m) => m.locked), [months]);
  const openMonths = useMemo(() => months.filter((m) => !m.locked), [months]);

  const lockMonth = async (ym, notes) => {
    if (!canLock) { toast.error('Only admins / super admins can lock months.'); return; }
    setBusyYm(ym);
    try {
      await api.post('/monthly-recon/close', { month: ym, notes: notes || null });
      toast.success(`${ymToLabel(ym)} locked.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Lock failed');
    } finally {
      setBusyYm(null);
    }
  };

  const unlockMonth = async (ym) => {
    if (!canLock) { toast.error('Only admins / super admins can unlock months.'); return; }
    if (!window.confirm(`Unlock ${ymToLabel(ym)}? Edits to journal entries / expenses in this period will be allowed again.`)) return;
    setBusyYm(ym);
    try {
      await api.post('/monthly-recon/reopen', { month: ym });
      toast.success(`${ymToLabel(ym)} unlocked.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unlock failed');
    } finally {
      setBusyYm(null);
    }
  };

  const lastMonthYm = lastNMonths(2)[1]; // index 0 is current, 1 is previous full month
  const lastMonthRow = months.find((m) => m.ym === lastMonthYm);
  const lastMonthAlreadyLocked = !!lastMonthRow?.locked;

  // iter712 — Bulk close all near-closed months.
  const [bulkMinPct, setBulkMinPct] = useState(97);
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null); // {candidates, skipped} or null
  const [bulkBusy, setBulkBusy] = useState(false);

  // iter713 — Monthly auto-lock scheduler (opt-in per tenant).
  const [autoLock, setAutoLock] = useState(null); // settings doc
  const [autoLockBusy, setAutoLockBusy] = useState(false);
  const [autoLockRuns, setAutoLockRuns] = useState([]);

  const loadAutoLock = async () => {
    try {
      const [{ data: s }, { data: runs }] = await Promise.all([
        api.get('/monthly-recon/auto-lock/settings'),
        api.get('/monthly-recon/auto-lock/runs?limit=5'),
      ]);
      setAutoLock(s);
      setAutoLockRuns(Array.isArray(runs) ? runs : []);
    } catch (e) {
      // Surface only as a console hint — the page must still work even
      // if the auto-lock service isn't reachable for some reason.
      console.warn('[period-locks] auto-lock fetch failed:', e?.response?.data?.detail || e?.message);
    }
  };
  useEffect(() => { loadAutoLock(); }, []);

  const saveAutoLock = async () => {
    if (!canLock || !autoLock) return;
    setAutoLockBusy(true);
    try {
      const { data } = await api.put('/monthly-recon/auto-lock/settings', autoLock);
      setAutoLock(data);
      toast.success(`Auto-lock ${data.enabled ? 'enabled' : 'disabled'}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setAutoLockBusy(false);
    }
  };

  const runAutoLockNow = async () => {
    if (!canLock) return;
    if (!window.confirm('Run an auto-lock pass right now? Months matching your saved criteria will be locked immediately.')) return;
    setAutoLockBusy(true);
    try {
      const { data } = await api.post('/monthly-recon/auto-lock/run-now');
      const n = (data.locked || []).length;
      if (n) toast.success(`Auto-lock locked ${n} month${n === 1 ? '' : 's'}.`);
      else toast.info('No months matched the criteria — nothing locked.');
      await Promise.all([load(), loadAutoLock()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setAutoLockBusy(false);
    }
  };

  const previewBulk = async () => {
    if (!canLock) { toast.error('Only admins / super admins can bulk-lock months.'); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post('/monthly-recon/bulk-close', {
        min_pct: bulkMinPct, dry_run: true, months_back: 24,
      });
      setBulkPreview(data);
      if ((data.candidates || []).length === 0) {
        toast.info(`No open months at or above ${bulkMinPct}% — nothing to lock.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const commitBulk = async () => {
    if (!canLock || !bulkPreview) return;
    if (!window.confirm(`Lock ${bulkPreview.candidates?.length || 0} month(s) at or above ${bulkPreview.min_pct}%? Journal entries / expenses in these periods will be sealed from edits.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/monthly-recon/bulk-close', {
        min_pct: bulkPreview.min_pct, notes: bulkNotes || null, months_back: 24,
      });
      const n = (data.locked || []).length;
      toast.success(`${n} month${n === 1 ? '' : 's'} locked.`);
      setBulkPreview(null);
      setBulkNotes('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk lock failed');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-red-700 flex items-center justify-center shadow-lg shadow-rose-200/50">
            <Lock size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="period-locks-title">Period Locks</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Seal closed months so journal entries / expenses cannot be edited or back-dated by mistake. Super admins can unlock anytime.
            </p>
          </div>
          {!canLock && (
            <Badge className="bg-amber-100 text-amber-900 border border-amber-200" data-testid="period-locks-readonly-badge">
              <AlertTriangle size={12} className="mr-1" /> Read-only · admin needed
            </Badge>
          )}
        </div>

        {/* Quick-lock last month */}
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <CalendarCheck2 size={16} className="text-emerald-600" /> Quick lock — last full month
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <p className="font-semibold text-foreground">{ymToLabel(lastMonthYm)}</p>
                <p className="text-xs text-muted-foreground">
                  {lastMonthAlreadyLocked
                    ? `Already locked by ${lastMonthRow.locked_by || 'admin'} on ${(lastMonthRow.locked_at || '').slice(0, 10)}.`
                    : 'Lock once you\'re happy with the books — guards against accidental edits in the past.'}
                </p>
              </div>
              <Button
                size="sm"
                className="rounded-xl"
                disabled={!canLock || lastMonthAlreadyLocked || busyYm === lastMonthYm}
                onClick={() => lockMonth(lastMonthYm)}
                data-testid="quick-lock-last-month-btn"
              >
                <Lock size={14} className="mr-1" />
                {lastMonthAlreadyLocked ? 'Locked' : busyYm === lastMonthYm ? 'Locking…' : `Lock ${ymToLabel(lastMonthYm)}`}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* iter712 — Bulk-close all near-closed months in one click. */}
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <Sparkles size={16} className="text-violet-600" />
              Bulk lock — close every near-finished month
              <Badge className="bg-violet-100 text-violet-800 border-violet-200 font-normal">Power user</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Locks every open month whose reconciliation is at or above the threshold below. Already-locked months are skipped. Step 1 previews the affected months; step 2 commits the lock.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label htmlFor="bulk-min-pct">Minimum completion (%)</Label>
                <Input
                  id="bulk-min-pct"
                  type="number"
                  min={50}
                  max={100}
                  step={1}
                  value={bulkMinPct}
                  onChange={(e) => { setBulkMinPct(Number(e.target.value) || 97); setBulkPreview(null); }}
                  data-testid="bulk-min-pct-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="bulk-notes">Notes (optional — applied to every locked month)</Label>
                <Input
                  id="bulk-notes"
                  placeholder="Q2 books signed off…"
                  value={bulkNotes}
                  onChange={(e) => setBulkNotes(e.target.value)}
                  data-testid="bulk-notes-input"
                />
              </div>
              <Button
                className="rounded-xl"
                variant="outline"
                disabled={!canLock || bulkBusy}
                onClick={previewBulk}
                data-testid="bulk-preview-btn"
              >
                <Sparkles size={14} className="mr-1" />
                {bulkBusy && !bulkPreview ? 'Scanning…' : 'Preview candidates'}
              </Button>
            </div>

            {bulkPreview && (
              <div className="rounded-xl border border-violet-200 bg-white/80 p-3 space-y-2" data-testid="bulk-preview-panel">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {(bulkPreview.candidates || []).length === 0
                      ? `Nothing to lock at ≥ ${bulkPreview.min_pct}%`
                      : `${(bulkPreview.candidates || []).length} month(s) ready to lock at ≥ ${bulkPreview.min_pct}%`}
                  </span>
                  <button type="button" onClick={() => setBulkPreview(null)} className="text-muted-foreground hover:text-foreground" aria-label="Close preview" data-testid="bulk-preview-close">
                    <X size={14} />
                  </button>
                </div>
                {(bulkPreview.candidates || []).length > 0 && (
                  <ul className="space-y-1 max-h-44 overflow-y-auto pr-1" data-testid="bulk-preview-list">
                    {bulkPreview.candidates.map((c) => (
                      <li key={c.ym} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-violet-50">
                        <span className="font-medium text-foreground">{ymToLabel(c.ym)}</span>
                        <span className="font-mono text-violet-800">{Math.round(c.completion_pct || 0)}%</span>
                      </li>
                    ))}
                  </ul>
                )}
                {(bulkPreview.skipped || []).length > 0 && (
                  <details className="text-[11px] text-muted-foreground">
                    <summary className="cursor-pointer">Skipped ({(bulkPreview.skipped || []).length})</summary>
                    <ul className="mt-1 space-y-0.5 pl-3 list-disc">
                      {bulkPreview.skipped.map((s, i) => (
                        <li key={i}>{ymToLabel(s.ym)} — {s.reason}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {(bulkPreview.candidates || []).length > 0 && (
                  <div className="flex justify-end">
                    <Button
                      className="rounded-xl bg-violet-600 hover:bg-violet-700"
                      disabled={!canLock || bulkBusy}
                      onClick={commitBulk}
                      data-testid="bulk-commit-btn"
                    >
                      <Lock size={14} className="mr-1" />
                      {bulkBusy ? 'Locking…' : `Lock ${bulkPreview.candidates.length} month${bulkPreview.candidates.length === 1 ? '' : 's'}`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter713 — Monthly auto-lock scheduler */}
        <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit flex items-center gap-2 flex-wrap">
              <Timer size={16} className="text-sky-600" />
              Auto-lock schedule
              <Badge className="bg-sky-100 text-sky-800 border-sky-200 font-normal">Set & forget</Badge>
              {autoLock?.enabled && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Enabled</Badge>}
              {autoLock && !autoLock.enabled && <Badge variant="outline">Disabled</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Once per month at the day & hour you pick, the cron locks every month from {autoLock?.lag_months ?? 2}+ months ago whose reconciliation is at or above the threshold. Recent months are never auto-locked (the lag protects you).
            </p>
            {autoLock && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end" data-testid="auto-lock-form">
                <div className="col-span-2 md:col-span-1 flex items-center gap-2 pt-5">
                  <input
                    id="auto-lock-enabled"
                    type="checkbox"
                    className="h-4 w-4 accent-sky-600"
                    checked={!!autoLock.enabled}
                    onChange={(e) => setAutoLock({ ...autoLock, enabled: e.target.checked })}
                    disabled={!canLock || autoLockBusy}
                    data-testid="auto-lock-enabled"
                  />
                  <Label htmlFor="auto-lock-enabled" className="cursor-pointer text-sm font-medium">Enabled</Label>
                </div>
                <div>
                  <Label htmlFor="auto-lock-pct">Min %</Label>
                  <Input
                    id="auto-lock-pct" type="number" min={50} max={100} step={1}
                    value={autoLock.min_pct}
                    onChange={(e) => setAutoLock({ ...autoLock, min_pct: Number(e.target.value) || 99 })}
                    disabled={!canLock || autoLockBusy}
                    data-testid="auto-lock-pct"
                  />
                </div>
                <div>
                  <Label htmlFor="auto-lock-lag">Lag (months)</Label>
                  <Input
                    id="auto-lock-lag" type="number" min={0} max={12} step={1}
                    value={autoLock.lag_months}
                    onChange={(e) => setAutoLock({ ...autoLock, lag_months: Number(e.target.value) || 0 })}
                    disabled={!canLock || autoLockBusy}
                    data-testid="auto-lock-lag"
                  />
                </div>
                <div>
                  <Label htmlFor="auto-lock-dom">Day of month</Label>
                  <select
                    id="auto-lock-dom"
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={autoLock.day_of_month}
                    onChange={(e) => setAutoLock({ ...autoLock, day_of_month: Number(e.target.value) })}
                    disabled={!canLock || autoLockBusy}
                    data-testid="auto-lock-dom"
                  >
                    {DOM.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="auto-lock-hour">Hour (UTC)</Label>
                  <select
                    id="auto-lock-hour"
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={autoLock.hour}
                    onChange={(e) => setAutoLock({ ...autoLock, hour: Number(e.target.value) })}
                    disabled={!canLock || autoLockBusy}
                    data-testid="auto-lock-hour"
                  >
                    {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                </div>
                <div className="col-span-2 md:col-span-2">
                  <Label htmlFor="auto-lock-email" className="flex items-center gap-1">
                    <Bell size={11} /> Notify email (optional)
                  </Label>
                  <Input
                    id="auto-lock-email" type="email"
                    placeholder="bookkeeper@…"
                    value={autoLock.notify_email || ''}
                    onChange={(e) => setAutoLock({ ...autoLock, notify_email: e.target.value || null })}
                    disabled={!canLock || autoLockBusy}
                    data-testid="auto-lock-email"
                  />
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-between items-center pt-1">
              <span className="text-[11px] text-muted-foreground">
                {autoLock?.updated_at && `Last saved ${String(autoLock.updated_at).slice(0, 10)} by ${autoLock.updated_by || 'admin'}.`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" className="rounded-xl"
                  disabled={!canLock || autoLockBusy || !autoLock?.enabled}
                  onClick={runAutoLockNow}
                  data-testid="auto-lock-run-now"
                >
                  <Play size={12} className="mr-1" />
                  {autoLockBusy ? 'Running…' : 'Run now (test)'}
                </Button>
                <Button
                  size="sm" className="rounded-xl bg-sky-600 hover:bg-sky-700"
                  disabled={!canLock || autoLockBusy}
                  onClick={saveAutoLock}
                  data-testid="auto-lock-save"
                >
                  {autoLockBusy ? 'Saving…' : 'Save schedule'}
                </Button>
              </div>
            </div>
            {autoLockRuns.length > 0 && (
              <details className="text-xs pt-1" data-testid="auto-lock-runs-block">
                <summary className="cursor-pointer text-sky-700 font-medium">Recent runs ({autoLockRuns.length})</summary>
                <ul className="mt-2 space-y-1">
                  {autoLockRuns.map((r, i) => (
                    <li key={i} className="flex items-start justify-between gap-2 p-2 rounded bg-white/60 border border-sky-100" data-testid={`auto-lock-run-${i}`}>
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-muted-foreground">{String(r.ran_at).slice(0, 16).replace('T', ' ')} · {r.trigger}</div>
                        <div className="text-[11px]">
                          {(r.locked || []).length === 0
                            ? <span className="text-muted-foreground">Locked 0 months.</span>
                            : <span className="text-emerald-700">Locked: {(r.locked || []).map((l) => l.ym).join(', ')}</span>}
                          {(r.skipped || []).length > 0 && <span className="text-amber-700"> · Skipped {(r.skipped || []).length}</span>}
                          {r.error && <span className="text-rose-700"> · {r.error}</span>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </CardContent>
        </Card>

        {/* Lock arbitrary month */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <ShieldCheck size={16} className="text-rose-600" /> Lock a specific month
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label htmlFor="lock-ym">Period (YYYY-MM)</Label>
              <Input id="lock-ym" type="month" value={customYm} onChange={(e) => setCustomYm(e.target.value)} data-testid="lock-custom-ym" />
            </div>
            <div className="md:col-span-1">
              <Label htmlFor="lock-notes">Notes (optional)</Label>
              <Input id="lock-notes" value={customNotes} placeholder="Closing approved by …" onChange={(e) => setCustomNotes(e.target.value)} data-testid="lock-custom-notes" />
            </div>
            <Button
              className="rounded-xl"
              disabled={!canLock || !customYm || busyYm === customYm}
              onClick={() => lockMonth(customYm, customNotes)}
              data-testid="lock-custom-btn"
            >
              <Lock size={14} className="mr-1" />
              {busyYm === customYm ? 'Locking…' : 'Lock month'}
            </Button>
          </CardContent>
        </Card>

        {/* Currently locked */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <Lock size={16} className="text-rose-600" />
              Currently locked
              <Badge variant="secondary" className="font-normal">{lockedMonths.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : lockedMonths.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="no-locked-months">No months are locked yet. Use the buttons above to seal a closed period.</p>
            ) : (
              <div className="space-y-2" data-testid="locked-months-list">
                {lockedMonths.map((m) => (
                  <div key={m.ym} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-rose-200 bg-rose-50/60" data-testid={`locked-row-${m.ym}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{ymToLabel(m.ym)}</p>
                      <p className="text-xs text-rose-700">
                        Locked by {m.locked_by || 'admin'}
                        {m.locked_at && ` · ${String(m.locked_at).slice(0, 10)}`}
                        {m.lock_notes ? ` · "${m.lock_notes}"` : ''}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-100"
                      disabled={!canLock || busyYm === m.ym}
                      onClick={() => unlockMonth(m.ym)}
                      data-testid={`unlock-row-btn-${m.ym}`}
                    >
                      <Unlock size={12} className="mr-1" />
                      {busyYm === m.ym ? 'Unlocking…' : 'Unlock'}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent open months */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <CalendarCheck2 size={16} className="text-emerald-600" />
              Recent open months
              <Badge variant="secondary" className="font-normal">{openMonths.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : openMonths.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing yet — upload a bank statement to populate this list.</p>
            ) : (
              <div className="space-y-2" data-testid="open-months-list">
                {openMonths.slice(0, 12).map((m) => {
                  const pct = Math.round(m.completion_pct || 0);
                  return (
                    <div key={m.ym} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-muted/30" data-testid={`open-row-${m.ym}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground truncate">{ymToLabel(m.ym)}</p>
                        <p className="text-xs text-muted-foreground">{m.linked_txns}/{m.total_txns} linked · {pct}%</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl"
                        disabled={!canLock || busyYm === m.ym}
                        onClick={() => lockMonth(m.ym)}
                        data-testid={`lock-row-btn-${m.ym}`}
                      >
                        <Lock size={12} className="mr-1" />
                        {busyYm === m.ym ? 'Locking…' : 'Lock'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
